import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';

// ─── Constants ──────────────────────────────────────────────────────────────

const TICKET_TYPES = [
  {
    id: 'general',
    label: 'General',
    description: 'General feedback or queries',
    icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
  },
  {
    id: 'indent',
    label: 'Indent Based',
    description: 'Issues related to indent orders',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  },
];

const INDENT_ISSUE_TYPES = [
  { id: 'pricing',                  label: 'Pricing Issue' },
  { id: 'quality',                  label: 'Quality Issue' },
  { id: 'quantity',                 label: 'Quantity Issue' },
  { id: 'despatched_not_delivered', label: 'Dispatched but Not Delivered' },
  { id: 'delayed_delivery',         label: 'Delayed Delivery' },
  { id: 'status_unknown',           label: 'Delivery Status Unknown' },
  { id: 'others',                   label: 'Others' },
];

const PERIOD_FILTERS = [
  { id: 'last_7_days',   label: 'Last 7 Days' },
  { id: 'last_30_days',  label: 'Last 30 Days' },
  { id: 'last_3_months', label: 'Last 3 Months' },
  { id: 'all',           label: 'All Time' },
];

// STATUS_FILTERS is derived dynamically from API data (see indentStatuses below)

// ─── Component ───────────────────────────────────────────────────────────────

const NewFeedback = () => {
  const navigate = useNavigate();

  // Form state
  const [ticketType, setTicketType]           = useState('');
  const [generalSubject, setGeneralSubject]   = useState('');
  const [generalBody, setGeneralBody]         = useState('');

  // Indent-based
  const [issueTypes, setIssueTypes]           = useState([]);
  const [periodFilter, setPeriodFilter]       = useState('');
  const [statusFilter, setStatusFilter]       = useState('');
  const [selectedIndents, setSelectedIndents] = useState([]);
  const [selectedItems, setSelectedItems]     = useState([]);
  const [remarks, setRemarks]                 = useState('');

  // Data
  const [indents, setIndents]                 = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [items, setItems]                     = useState([]);
  const [loadingIndents, setLoadingIndents]   = useState(false);
  const [loadingItems, setLoadingItems]       = useState(false);

  // Images
  const [uploadedImages, setUploadedImages]         = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls]     = useState([]);
  const [uploadedImagePaths, setUploadedImagePaths] = useState([]);
  const [uploadingIndexes, setUploadingIndexes]     = useState([]);

  // Submit
  const [submitting, setSubmitting]     = useState(false);
  const [toast, setToast]               = useState(null); // { msg, type }
  const [formErrors, setFormErrors]     = useState({});   // field-level inline errors

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Data fetching ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (ticketType === 'indent') fetchIndents();
  }, [ticketType]);

  useEffect(() => {
    if (ticketType === 'indent') applyFilters();
  }, [periodFilter, statusFilter, indents]);

  useEffect(() => {
    if (selectedIndents.length > 0) fetchItems();
    else { setItems([]); setSelectedItems([]); }
  }, [selectedIndents]);

  const fetchIndents = async () => {
    try {
      setLoadingIndents(true);
      const token = localStorage.getItem('authToken');
      const res = await axios.get(`${API_BASE_URL}/indent/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = !res.data.error && res.data.data ? res.data.data : [];
      setIndents(data);
      setFilteredIndents(data);
    } catch (err) {
      setIndents([]); setFilteredIndents([]);
      if (err.response?.status === 401) { localStorage.removeItem('authToken'); window.location.href = '/login'; }
    } finally { setLoadingIndents(false); }
  };

  const applyFilters = () => {
    let list = [...indents];
    if (periodFilter && periodFilter !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      if (periodFilter === 'last_7_days')   cutoff.setDate(now.getDate() - 7);
      if (periodFilter === 'last_30_days')  cutoff.setDate(now.getDate() - 30);
      if (periodFilter === 'last_3_months') cutoff.setMonth(now.getMonth() - 3);
      list = list.filter(i => new Date(i.created) >= cutoff);
    }
    if (statusFilter && statusFilter !== 'all') {
      list = list.filter(i => i.status?.toLowerCase() === statusFilter.toLowerCase());
    }
    setFilteredIndents(list);
  };

  const fetchItems = async () => {
    try {
      setLoadingItems(true);
      const token = localStorage.getItem('authToken');
      const responses = await Promise.all(
        selectedIndents.map(id =>
          axios.get(`${API_BASE_URL}/indent/${id}/detail`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      const all = responses.flatMap(r => (!r.data.error && r.data.data ? r.data.data : []));
      setItems(all);
    } catch { setItems([]); }
    finally { setLoadingItems(false); }
  };

  // ── Image upload ───────────────────────────────────────────────────────────

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files).slice(0, 5 - uploadedImages.length);
    e.target.value = '';
    if (!files.length) return;
    const token = localStorage.getItem('authToken');

    for (const file of files) {
      const preview = URL.createObjectURL(file);
      const idx = uploadedImages.length + files.indexOf(file);
      setUploadedImages(p => [...p, file]);
      setImagePreviewUrls(p => [...p, preview]);
      setUploadedImagePaths(p => [...p, null]);
      setUploadingIndexes(p => [...p, idx]);

      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_BASE_URL}/feedback/upload/attachment`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        setUploadedImagePaths(p => { const u = [...p]; u[u.lastIndexOf(null)] = res.data?.file || ''; return u; });
      } catch {
        setUploadedImages(p => p.filter(f => f !== file));
        setImagePreviewUrls(p => { const i = p.indexOf(preview); URL.revokeObjectURL(preview); return p.filter((_, j) => j !== i); });
        setUploadedImagePaths(p => { const i = p.lastIndexOf(null); return p.filter((_, j) => j !== i); });
        showToast(`Failed to upload ${file.name}. Please try again.`, 'error');
      } finally {
        setUploadingIndexes(p => p.filter(i => i !== idx));
      }
    }
  };

  const removeImage = (i) => {
    URL.revokeObjectURL(imagePreviewUrls[i]);
    setUploadedImages(p => p.filter((_, j) => j !== i));
    setImagePreviewUrls(p => p.filter((_, j) => j !== i));
    setUploadedImagePaths(p => p.filter((_, j) => j !== i));
  };

  // ── Type change ────────────────────────────────────────────────────────────

  const handleTypeChange = (type) => {
    setTicketType(type);
    setGeneralSubject(''); setGeneralBody('');
    setIssueTypes([]); setPeriodFilter(''); setStatusFilter('');
    setSelectedIndents([]); setSelectedItems([]); setRemarks('');
    setIndents([]); setFilteredIndents([]); setItems([]);
    setFormErrors({});
    imagePreviewUrls.forEach(u => URL.revokeObjectURL(u));
    setUploadedImages([]); setImagePreviewUrls([]); setUploadedImagePaths([]); setUploadingIndexes([]);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (ticketType === 'general') {
      if (!generalSubject.trim()) errors.generalSubject = 'Subject is required';
      if (!generalBody.trim())    errors.generalBody    = 'Description is required';
    }
    if (ticketType === 'indent') {
      if (!issueTypes.length)      errors.issueTypes      = 'Select at least one issue type';
      if (!selectedIndents.length) errors.selectedIndents = 'Select at least one indent';
      if (!remarks.trim())         errors.remarks         = 'Remarks are required';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    try {
      setSubmitting(true);
      const token = localStorage.getItem('authToken');
      const imagePaths = uploadedImagePaths.filter(Boolean);

      let endpoint, payload;
      if (ticketType === 'general') {
        endpoint = `${API_BASE_URL}/feedback/general`;
        payload  = { subject: generalSubject, remarks: generalBody, ...(imagePaths.length && { image_paths: imagePaths }) };
      } else {
        endpoint = `${API_BASE_URL}/feedback/indent`;
        payload  = { issue_types: issueTypes, indent_ids: selectedIndents, item_ids: selectedItems, remarks, ...(imagePaths.length && { image_paths: imagePaths }) };
      }

      const res = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!res.data.error) {
        showToast('Feedback submitted successfully!');
        setTimeout(() => navigate('/feed-back'), 1500);
      } else { showToast(res.data.message || 'Failed to submit feedback', 'error'); }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to submit feedback. Please try again.', 'error');
      if (err.response?.status === 401) { localStorage.removeItem('authToken'); window.location.href = '/login'; }
    } finally { setSubmitting(false); }
  };

  const imagesUploading = uploadedImagePaths.some(p => p === null);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex bg-slate-50 min-h-full">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-slate-200 flex flex-col flex-shrink-0 overflow-y-auto">
        <div className="p-4 border-b border-slate-100">
          <div className="w-full flex items-center gap-2.5 bg-blue-600 text-white text-sm font-semibold py-2.5 px-4 rounded-lg shadow-sm">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            New Feedback
          </div>
        </div>
        <nav className="flex-1 px-3 py-3 space-y-0.5">
          <button
            onClick={() => navigate('/feed-back')}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Feedback
          </button>
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 overflow-y-auto">
        {/* Page header */}
        <div className="bg-white border-b border-slate-200 px-8 py-4 flex items-center gap-3 sticky top-0 z-10">
          <button
            onClick={() => navigate('/feed-back')}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-baseline gap-3">
            <h1 className="text-sm font-bold text-slate-800">Submit New Feedback</h1>
            <span className="text-[10px] text-slate-400">Fill in the details below</span>
          </div>
        </div>

        <div className="px-8 py-6">
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* ── Step 1: Ticket type ── */}
            <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80 flex items-center gap-2.5">
                <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">1</span>
                <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Feedback Type</h2>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                {TICKET_TYPES.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleTypeChange(type.id)}
                    disabled={submitting}
                    className={`flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                      ticketType === type.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    } disabled:opacity-50`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      ticketType === type.id ? 'bg-blue-600' : 'bg-slate-100'
                    }`}>
                      <svg className={`w-5 h-5 ${ticketType === type.id ? 'text-white' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d={type.icon} />
                      </svg>
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${ticketType === type.id ? 'text-blue-700' : 'text-slate-700'}`}>
                        {type.label}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">{type.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </section>

            {/* ── General feedback fields ── */}
            {ticketType === 'general' && (
              <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80 flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">2</span>
                  <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Feedback Details</h2>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={generalSubject}
                      onChange={e => { setGeneralSubject(e.target.value); setFormErrors(p => ({ ...p, generalSubject: '' })); }}
                      placeholder="Brief summary of your issue"
                      disabled={submitting}
                      className={`w-full px-4 py-2.5 bg-slate-50 border rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition disabled:opacity-60 ${formErrors.generalSubject ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                    />
                    {formErrors.generalSubject && (
                      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        {formErrors.generalSubject}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={generalBody}
                      onChange={e => { setGeneralBody(e.target.value); setFormErrors(p => ({ ...p, generalBody: '' })); }}
                      placeholder="Provide a detailed description of your feedback or issue…"
                      rows="5"
                      disabled={submitting}
                      className={`w-full px-4 py-2.5 bg-slate-50 border rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition resize-none disabled:opacity-60 ${formErrors.generalBody ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                    />
                    {formErrors.generalBody && (
                      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        {formErrors.generalBody}
                      </p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* ── Indent-based fields ── */}
            {ticketType === 'indent' && (
              <>
                {/* Issue types */}
                <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80 flex items-center gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">2</span>
                    <div>
                      <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Issue Type</h2>
                      <p className="text-[10px] text-slate-400 mt-0.5">Select all that apply</p>
                    </div>
                  </div>
                  <div className="p-4 grid grid-cols-2 gap-2">
                    {INDENT_ISSUE_TYPES.map(issue => {
                      const active = issueTypes.includes(issue.id);
                      return (
                        <label
                          key={issue.id}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all select-none ${
                            active ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          } ${submitting ? 'opacity-50 pointer-events-none' : ''}`}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            active ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                          }`}>
                            {active && (
                              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={active}
                            onChange={() => { setIssueTypes(p => active ? p.filter(t => t !== issue.id) : [...p, issue.id]); setFormErrors(p => ({ ...p, issueTypes: '' })); }}
                            disabled={submitting}
                          />
                          <span className={`text-sm font-medium ${active ? 'text-blue-700' : 'text-slate-700'}`}>
                            {issue.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {formErrors.issueTypes && (
                    <div className="px-5 pb-4">
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        {formErrors.issueTypes}
                      </p>
                    </div>
                  )}
                </section>

                {/* Indent selection */}
                <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">3</span>
                      <div>
                        <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Select Indents</h2>
                        <p className="text-[10px] text-slate-400 mt-0.5">Select one or more indents related to this issue</p>
                      </div>
                    </div>
                    {selectedIndents.length > 0 && (
                      <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full uppercase tracking-wide">
                        {selectedIndents.length} selected
                      </span>
                    )}
                  </div>

                  {/* Filters */}
                  <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex gap-3">
                    <select
                      value={periodFilter}
                      onChange={e => setPeriodFilter(e.target.value)}
                      disabled={submitting}
                      className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-400 transition disabled:opacity-60"
                    >
                      <option value="">Filter by period</option>
                      {PERIOD_FILTERS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                    <select
                      value={statusFilter}
                      onChange={e => setStatusFilter(e.target.value)}
                      disabled={submitting}
                      className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-400 transition disabled:opacity-60"
                    >
                      <option value="">Filter by status</option>
                      {[...new Set(indents.map(i => i.status).filter(Boolean))].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="divide-y divide-slate-100">
                    {loadingIndents ? (
                      <div className="flex justify-center items-center py-8 gap-2">
                        <svg className="w-5 h-5 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        <span className="text-sm text-slate-400">Loading indents…</span>
                      </div>
                    ) : filteredIndents.length === 0 ? (
                      <div className="flex justify-center items-center py-8">
                        <p className="text-sm text-slate-400">No indents available</p>
                      </div>
                    ) : (
                      filteredIndents.map(indent => {
                        const active = selectedIndents.includes(indent.id);
                        return (
                          <label
                            key={indent.id}
                            className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors select-none ${
                              active ? 'bg-blue-50' : 'hover:bg-slate-50'
                            } ${submitting ? 'opacity-50 pointer-events-none' : ''}`}
                          >
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                              active ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                            }`}>
                              {active && (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={active}
                              onChange={() => { setSelectedIndents(p => active ? p.filter(id => id !== indent.id) : [...p, indent.id]); setFormErrors(p => ({ ...p, selectedIndents: '' })); }}
                              disabled={submitting}
                            />
                            <span className={`text-sm font-semibold ${active ? 'text-blue-700' : 'text-slate-600'}`}>
                              #{indent.id}
                            </span>
                            <span className="text-xs text-slate-400">{indent.created}</span>
                            <span className="ml-auto text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                              {indent.status}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  {formErrors.selectedIndents && (
                    <div className="px-5 py-3 border-t border-slate-100">
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        {formErrors.selectedIndents}
                      </p>
                    </div>
                  )}
                </section>

                {/* Items */}
                {selectedIndents.length > 0 && (
                  <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">4</span>
                        <div>
                          <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Select Items</h2>
                          <p className="text-[10px] text-slate-400 mt-0.5">Optional — narrow down to specific items</p>
                        </div>
                      </div>
                      {selectedItems.length > 0 && (
                        <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full uppercase tracking-wide">
                          {selectedItems.length} selected
                        </span>
                      )}
                    </div>

                    <div className="divide-y divide-slate-100">
                      {loadingItems ? (
                        <div className="flex justify-center items-center py-6 gap-2">
                          <svg className="w-5 h-5 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          <span className="text-sm text-slate-400">Loading items…</span>
                        </div>
                      ) : items.length === 0 ? (
                        <div className="flex justify-center items-center py-6">
                          <p className="text-sm text-slate-400">No items found</p>
                        </div>
                      ) : (
                        items.map(item => {
                          const active = selectedItems.includes(item.id);
                          return (
                            <label
                              key={item.id}
                              className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors select-none ${
                                active ? 'bg-blue-50' : 'hover:bg-slate-50'
                              } ${submitting ? 'opacity-50 pointer-events-none' : ''}`}
                            >
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                active ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                              }`}>
                                {active && (
                                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={active}
                                onChange={() => setSelectedItems(p => active ? p.filter(id => id !== item.id) : [...p, item.id])}
                                disabled={submitting}
                              />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${active ? 'text-blue-700' : 'text-slate-700'}`}>
                                  {item.name}{item.brand ? ` — ${item.brand}` : ''}
                                </p>
                                <p className="text-xs text-slate-400">{item.qty} {item.unit}</p>
                              </div>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </section>
                )}

                {/* Remarks */}
                <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80 flex items-center gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                      {selectedIndents.length > 0 ? '5' : '4'}
                    </span>
                    <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Remarks</h2>
                  </div>
                  <div className="p-4">
                    <textarea
                      value={remarks}
                      onChange={e => { setRemarks(e.target.value); setFormErrors(p => ({ ...p, remarks: '' })); }}
                      placeholder="Describe the issue in detail…"
                      rows="4"
                      disabled={submitting}
                      className={`w-full px-4 py-2.5 bg-slate-50 border rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition resize-none disabled:opacity-60 ${formErrors.remarks ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                    />
                    {formErrors.remarks && (
                      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        {formErrors.remarks}
                      </p>
                    )}
                  </div>
                </section>
              </>
            )}

            {/* ── Attachments (all types) ── */}
            {ticketType && (
              <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80 flex items-center gap-2.5">
                  <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <div>
                    <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Attachments</h2>
                    <p className="text-[10px] text-slate-400 mt-0.5">Optional — up to 5 files (images or PDF)</p>
                  </div>
                </div>
                <div className="p-4">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    id="img-upload"
                    className="sr-only"
                    disabled={submitting || uploadedImages.length >= 5}
                    onChange={handleImageUpload}
                  />
                  {uploadedImages.length < 5 && (
                    <label
                      htmlFor="img-upload"
                      className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-6 transition-colors ${
                        submitting ? 'opacity-50 cursor-not-allowed border-slate-200' : 'border-slate-300 hover:border-blue-400 cursor-pointer'
                      }`}
                    >
                      <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm font-medium text-blue-600">Click to upload files</p>
                      <p className="text-xs text-slate-400">PNG, JPG, PDF — up to 10 MB each</p>
                    </label>
                  )}

                  {imagePreviewUrls.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-3">
                      {imagePreviewUrls.map((url, i) => {
                        const uploading = uploadedImagePaths[i] === null;
                        const isPdf = uploadedImages[i]?.type === 'application/pdf';
                        return (
                          <div key={i} className="relative group w-20 h-20">
                            {isPdf ? (
                              <div className={`w-full h-full rounded-lg border-2 flex flex-col items-center justify-center gap-1 bg-red-50 ${uploading ? 'border-amber-400 opacity-60' : 'border-emerald-400'}`}>
                                <svg className="w-7 h-7 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8.5 15.5h-1v-4h1.8c.9 0 1.4.5 1.4 1.3 0 .9-.5 1.4-1.4 1.4H8.5v1.3zm0-2.2h.7c.4 0 .6-.2.6-.6s-.2-.6-.6-.6H8.5v1.2zm4.2 2.2h-1.5v-4h1.5c1.2 0 1.9.8 1.9 2s-.7 2-1.9 2zm-.7-.9h.6c.7 0 1.1-.4 1.1-1.1 0-.7-.4-1.1-1.1-1.1h-.6v2.2zm4.3-3.1v.9h1.4v.8h-1.4v1.4h-1v-4h2.6v.9H16.3z"/>
                                </svg>
                                <span className="text-xs text-red-500 font-semibold leading-tight text-center px-1 truncate w-full text-center">PDF</span>
                              </div>
                            ) : (
                              <img
                                src={url}
                                alt=""
                                className={`w-full h-full object-cover rounded-lg border-2 ${uploading ? 'border-amber-400 opacity-60' : 'border-emerald-400'}`}
                              />
                            )}
                            {uploading ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                                <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                              </div>
                            ) : (
                              <div className="absolute top-1 left-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              disabled={submitting || uploading}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full text-xs flex items-center justify-center font-bold shadow transition-colors disabled:opacity-50"
                            >×</button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {imagesUploading && (
                    <p className="mt-3 text-xs font-medium text-amber-600 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Uploading files…
                    </p>
                  )}
                </div>
              </section>
            )}

            {/* ── Actions ── */}
            {ticketType && (
              <div className="flex items-center justify-end gap-3 pt-1 pb-8 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => navigate('/feed-back')}
                  disabled={submitting}
                  className="px-6 py-2.5 rounded-lg text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || imagesUploading}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={imagesUploading ? 'Please wait for images to finish uploading' : ''}
                >
                  {submitting ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Submitting…
                    </>
                  ) : imagesUploading ? (
                    'Uploading images…'
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Submit Feedback
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
          <div className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-white text-sm font-medium ${
            toast.type === 'error' ? 'bg-red-600' : 'bg-emerald-600'
          }`}>
            {toast.type === 'error' ? (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
              </svg>
            )}
            {toast.msg}
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(16px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.25s ease-out; }
      `}</style>
    </div>
  );
};

export default NewFeedback;
